import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Search, ShieldCheck, Wallet, SlidersHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtPrice } from "@/lib/utils";
import { searchDayUse, createDayUseReservation } from "@/lib/api";
import { DayUseBookingModal } from "@/components/DayUseBookingModal";
import { DayUseHotelCard } from "@/components/DayUseHotelCard";

const today = () => new Date().toISOString().slice(0, 10);
const HOURS = Array.from({ length: 17 }, (_, i) => i + 7); // 07h -> 23h
const hh = (h) => `${String(h).padStart(2, "0")}h`;

const Field = ({ label, children, testid }) => (
  <label className="flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wider text-[var(--ink-soft)]" data-testid={testid}>
    {label}
    {children}
  </label>
);

export default function HotelParHeurePage() {
  const [f, setF] = useState({ city: "", date: today(), duree: "", heure_arrivee: "", heure_depart: "", prix_max: "", tri: "popularite" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  const params = useMemo(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== null)), [f]);
  const search = async () => {
    setLoading(true);
    try { setData(await searchDayUse(params)); }
    catch { toast.error("Recherche impossible pour le moment"); }
    finally { setLoading(false); }
  };
  useEffect(() => { search(); }, [f.date, f.duree, f.heure_arrivee, f.heure_depart, f.prix_max, f.tri]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => setF({ city: "", date: today(), duree: "", heure_arrivee: "", heure_depart: "", prix_max: "", tri: "popularite" });
  const activeFilters = ["duree", "heure_arrivee", "heure_depart", "prix_max"].filter((k) => f[k] !== "").length;

  return (
    <main className="bg-[#faf8f4] min-h-screen" data-testid="day-use-page">
      <section className="bg-[#1b1a17] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-300">Day-use · Hotel a l&apos;heure</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3" data-testid="day-use-hero-title">Hotels en journee{f.city ? ` a ${f.city}` : " en Cote d'Ivoire"}</h1>
          <p className="mt-3 text-white/75 max-w-2xl text-base">Chambres de jour jusqu&apos;a -50 % par rapport a la nuit. Choisissez votre creneau, reservez sans carte, payez a l&apos;hotel.</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/85">
            <span className="inline-flex items-center gap-2"><Clock size={16} className="text-orange-300" /> Creneaux de 2 a 6 h</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-orange-300" /> Annulation gratuite</span>
            <span className="inline-flex items-center gap-2"><Wallet size={16} className="text-orange-300" /> Paiement sur place</span>
          </div>
        </div>
      </section>

      <section className="sticky top-24 z-30 bg-white/95 backdrop-blur border-b border-neutral-200 shadow-sm" data-testid="day-use-filters">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 items-end">
          <Field label="Ville" testid="day-use-field-city">
            <input placeholder="Abidjan, Bouake..." value={f.city} onChange={(e) => set("city", e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="!mb-0 !py-2 text-sm" data-testid="day-use-city" />
          </Field>
          <Field label="Quand ?" testid="day-use-field-date">
            <input type="date" value={f.date} min={today()} onChange={(e) => set("date", e.target.value)} className="!mb-0 !py-2 text-sm" data-testid="day-use-date" />
          </Field>
          <Field label="Arrivee" testid="day-use-field-arrivee">
            <select value={f.heure_arrivee} onChange={(e) => set("heure_arrivee", e.target.value)} className="!mb-0 !py-2 text-sm" data-testid="day-use-heure-arrivee">
              <option value="">Indifferent</option>
              {HOURS.map((h) => <option key={h} value={h}>des {hh(h)}</option>)}
            </select>
          </Field>
          <Field label="Depart" testid="day-use-field-depart">
            <select value={f.heure_depart} onChange={(e) => set("heure_depart", e.target.value)} className="!mb-0 !py-2 text-sm" data-testid="day-use-heure-depart">
              <option value="">Indifferent</option>
              {HOURS.map((h) => <option key={h} value={h}>avant {hh(h)}</option>)}
            </select>
          </Field>
          <Field label="Duree" testid="day-use-field-duree">
            <select value={f.duree} onChange={(e) => set("duree", e.target.value)} className="!mb-0 !py-2 text-sm" data-testid="day-use-duree">
              <option value="">Toutes</option>
              {(data?.durees || [2, 3, 4, 6]).map((d) => <option key={d} value={d}>{d} heures</option>)}
            </select>
          </Field>
          <Field label="Prix max" testid="day-use-field-prix">
            <select value={f.prix_max} onChange={(e) => set("prix_max", e.target.value)} className="!mb-0 !py-2 text-sm" data-testid="day-use-prix-max">
              <option value="">Tous</option>
              {[25000, 50000, 75000, 100000, 150000].map((p) => <option key={p} value={p}>{fmtPrice(p)}</option>)}
            </select>
          </Field>
          <Field label="Trier par" testid="day-use-field-tri">
            <select value={f.tri} onChange={(e) => set("tri", e.target.value)} className="!mb-0 !py-2 text-sm" data-testid="day-use-tri">
              <option value="popularite">Popularite</option>
              <option value="prix">Prix croissant</option>
              <option value="reduction">Meilleure reduction</option>
            </select>
          </Field>
          <button onClick={search} className="btn-primary inline-flex items-center justify-center gap-2 !py-2.5" data-testid="day-use-search"><Search size={16} /> Rechercher</button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-lg font-bold" data-testid="day-use-count">
            {loading ? <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Recherche...</span>
              : <>{data?.disponibles || 0} hotel{(data?.disponibles || 0) > 1 ? "s" : ""} disponible{(data?.disponibles || 0) > 1 ? "s" : ""} <span className="text-[var(--ink-soft)] font-normal">le {f.date}</span></>}
          </h2>
          {activeFilters > 0 && (
            <button onClick={reset} className="chip inline-flex items-center gap-1 text-sm" data-testid="day-use-reset"><SlidersHorizontal size={14} /> Effacer {activeFilters} filtre{activeFilters > 1 ? "s" : ""}</button>
          )}
        </div>

        {data && data.count === 0 && (
          <div className="bg-white rounded-3xl p-10 text-center text-[var(--ink-soft)]" data-testid="day-use-empty">
            Aucun hotel ne propose encore l&apos;offre par heure pour cette recherche. <Link to="/hotelleries" className="text-orange-600 font-semibold">Voir tous les hotels</Link>
          </div>
        )}

        <div className="flex flex-col gap-6">
          {(data?.hotels || []).map((h, i) => <DayUseHotelCard key={h.id} hotel={h} index={i} onPick={(hotel, slot) => setBooking({ hotel, slot })} />)}
        </div>
      </section>

      {booking && (
        <DayUseBookingModal hotel={booking.hotel} slot={booking.slot} onClose={() => setBooking(null)} onBooked={search} createReservation={createDayUseReservation} />
      )}
    </main>
  );
}
